/**
 * The runs a reviewer has starred, and where they are kept.
 *
 * A favorite is a bookmark, not a fact about the run: two people looking at the
 * same pipeline star different runs, and nothing downstream of the picker cares
 * which. So it lives in `localStorage` rather than on the execution — the same
 * reasoning as the pane widths, and the opposite of the queue's filters, which
 * are in the URL precisely because they ARE worth sending to someone else.
 *
 * Stored as a plain array of execution ids. A `Set` is what the picker wants to
 * ask questions of, but JSON has no set, and an array survives being read by a
 * human in devtools — which is the only other thing that ever looks at this key.
 */

const FAVORITE_RUNS_STORAGE_KEY = 'gap-review:favorite-runs';

/**
 * `localStorage` can throw outright (Safari private browsing), and a picker that
 * cannot draw its list is a worse outcome than one that forgets a star, so every
 * read falls back to "nothing starred" and every write to a no-op.
 */
export function readStoredFavoriteRuns(): Set<string> {
  try {
    const stored = window.localStorage.getItem(FAVORITE_RUNS_STORAGE_KEY);
    if (stored === null) return new Set();

    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set();

    return new Set(
      parsed.filter(
        (executionId): executionId is string => typeof executionId === 'string'
      )
    );
  } catch {
    return new Set();
  }
}

export function writeStoredFavoriteRuns(
  favoriteRunIds: ReadonlySet<string>
): void {
  try {
    window.localStorage.setItem(
      FAVORITE_RUNS_STORAGE_KEY,
      JSON.stringify([...favoriteRunIds])
    );
  } catch {
    // A star that does not survive a reload is still worth having.
  }
}

/** Starred → unstarred and back, as a new set rather than a mutation. */
export function toggleFavoriteRun(
  favoriteRunIds: ReadonlySet<string>,
  executionId: string
): Set<string> {
  const nextFavoriteRunIds = new Set(favoriteRunIds);

  if (!nextFavoriteRunIds.delete(executionId)) {
    nextFavoriteRunIds.add(executionId);
  }

  return nextFavoriteRunIds;
}
